import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ["tab", "content"]
  static values = {
    initialActiveTab: Number,
    tabStyle: String,
    syncEmitEvent: String,
    syncListenEvent: String,
    syncIndexOffset: Number,
    urlParamName: String,
    urlParamValues: Array,
  }

  connect() {
    this.showTab(this.initialActiveTabValue, { clearErrors: false })

    if (this.hasSyncListenEventValue) {
      this._onSyncTabChange = (e) => {
        const targetIndex = e.detail.propertyIndex - this.syncIndexOffsetValue
        const clampedIndex = Math.max(0, Math.min(targetIndex, this.tabTargets.length - 1))
        this.showTab(clampedIndex)
      }
      window.addEventListener(this.syncListenEventValue, this._onSyncTabChange)
    }
  }

  disconnect() {
    if (this._onSyncTabChange && this.hasSyncListenEventValue) {
      window.removeEventListener(this.syncListenEventValue, this._onSyncTabChange)
    }
  }

  switchTab(event) {
    const tabIndex = parseInt(event.currentTarget.dataset.tabIndex)
    this.showTab(tabIndex)
    this._persistActiveTabInUrl(tabIndex)
    this._emitSyncEvent(tabIndex)
  }

  showTab(index, { clearErrors = true } = {}) {
    this.contentTargets.forEach(content => {
      content.classList.add("hidden")
    })

    if (this.tabStyleValue === "rounded") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-black-50", "hover:bg-black-100")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.add("bg-black-50", "hover:bg-black-100")
      }
    } else if (this.tabStyleValue === "pill") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-white", "shadow-sm", "text-brand-1000")
        tab.classList.add("hover:bg-white/50", "text-black-500")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("hover:bg-white/50", "text-black-500")
        selectedTab.classList.add("bg-white", "shadow-sm", "text-brand-1000")
      }
    } else if (this.tabStyleValue === "brand") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-brand-1000", "text-white")
        tab.classList.add("text-black-500", "hover:bg-white", "hover:text-brand-1000")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("text-black-500", "hover:bg-white", "hover:text-brand-1000")
        selectedTab.classList.add("bg-brand-1000", "text-white")
      }
    } else if (this.tabStyleValue === "white-brand") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-white", "text-brand-1000", "hover:bg-lightGray-1000")
        tab.classList.add("text-black-500", "hover:bg-white", "hover:text-brand-1000")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("text-black-500", "hover:bg-white", "hover:text-brand-1000")
        selectedTab.classList.add("bg-white", "text-brand-1000", "hover:bg-lightGray-1000")
      }
    } else if (this.tabStyleValue === "brand-inversed") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-brand-50", "shadow-[0_0_0_1px_theme(colors.brand.200)]")
        tab.classList.add("bg-transparent")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("bg-transparent")
        selectedTab.classList.add("bg-brand-50", "shadow-[0_0_0_1px_theme(colors.brand.200)]")
      }
    } else if (this.tabStyleValue === "default") {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("text-brand-1000", "border-brand-1000")
        tab.classList.add("text-black-600", "border-transparent", "hover:text-black-1000")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("text-black-600", "border-transparent", "hover:text-black-1000")
        selectedTab.classList.add("text-brand-1000", "border-brand-1000")
      }
    } else {
      this.tabTargets.forEach(tab => {
        tab.classList.remove("bg-black-50", "hover:bg-black-100")
        tab.classList.add("hover:bg-black-50")
      })

      const selectedTab = this.tabTargets.find(tab =>
        parseInt(tab.dataset.tabIndex) === index
      )
      if (selectedTab) {
        selectedTab.classList.remove("hover:bg-black-50")
        selectedTab.classList.add("bg-black-50", "hover:bg-black-100")
      }
    }

    this.contentTargets
      .filter(content => parseInt(content.dataset.tabIndex) === index)
      .forEach(content => {
        content.classList.remove("hidden")
        if (clearErrors) this._clearStaleValidationErrors(content)
      })
  }

  _clearStaleValidationErrors(content) {
    content.querySelectorAll('[data-backend-error="true"]')
      .forEach(el => el.remove())
    content.querySelectorAll('[data-trix-input-target="wrapper"].border-red-300')
      .forEach(el => el.classList.remove('border-red-300'))
  }

  _persistActiveTabInUrl(index) {
    if (!this.hasUrlParamNameValue || !this.hasUrlParamValuesValue) return

    const value = this.urlParamValuesValue[index]
    const url = new URL(window.location.href)

    if (value === null || value === undefined || value === "") {
      url.searchParams.delete(this.urlParamNameValue)
    } else {
      url.searchParams.set(this.urlParamNameValue, value)
    }

    window.history.replaceState(window.history.state, "", url.toString())
  }

  _emitSyncEvent(index) {
    if (this.hasSyncEmitEventValue) {
      const propertyIndex = Math.max(0, index + this.syncIndexOffsetValue)
      window.dispatchEvent(new CustomEvent(this.syncEmitEventValue, {
        detail: { propertyIndex },
      }))
    }
  }
}
